<template>
  <Popup
    :title="popupTitle"
    :sub-title="currentAddress"
    :disabled="isSubmitting"
    :loading="isSubmitting"
    :btn-text="submitButtonText"
    type="submit"
    @close="$emit('close')"
    @approve="handleSubmit"
  >
    <div class="add-panel">
      <Tabs
        :tabs="steps"
        :value="currentStep"
        @update:modelValue="step => $store.dispatch('setCurrentStep', step)"
      />
      <form
        class="add-panel__form"
        action="post"
        @submit.prevent="handleSubmit"
      >
        <component
          :is="currentStep"
          :is-generating-link="isGeneratingLink"
          @generateLink="handleGenerateLink"
        />
      </form>
    </div>
  </Popup>
</template>

<script>
import Popup from '../../components/Popup.vue'
import Tabs from '../../components/Tabs.vue'
import AddPanelBasic from './forms/AddPanelBasic.vue'
import AddPanelNetwork from './forms/AddPanelNetwork.vue'
import AddPanelAccess from './forms/AddPanelAccess.vue'
import AddPanelUsers from './forms/AddPanelUsers.vue'
import ServiceData from '../../mixins/ServiceData.js'
import {
  validatePanelData,
  getErrorMessage,
  getFirstErrorStep,
} from '../../validation/PanelValidation.js'
import {
  hasSharedCredentials,
  getSharedCredentials,
} from '../../utils/panelCredentials.js'

function resolveCredentials(network) {
  if (!network.applyToAll) {
    return { login: network.login || '', password: network.password || '' }
  }
  const shared = hasSharedCredentials() ? getSharedCredentials() : null
  return { login: shared?.login || '', password: shared?.password || '' }
}

function buildDevicePayload(accessItem) {
  const payload = {
    device_type_id: accessItem.deviceTypeId,
    lock_type_id: accessItem.lockTypeId || 1,
    uses_api: !!accessItem.usesApi,
    dtmf: accessItem.usesApi ? '' : accessItem.dtmf || '',
    open_link: accessItem.openLink || '',
    is_active: accessItem.isActive !== false,
  }

  if (accessItem.deviceId) {
    payload.device_id = accessItem.deviceId
    if (accessItem.regenerateLink) {
      payload.regenerate_link = true
    }
  }

  return payload
}

function buildSectionPayload(sectionId, availableSections) {
  const section = availableSections.find(s => s.id === sectionId)
  return {
    section_id: sectionId,
    section_name: section?.name || '',
    section_index: section?.index || 0,
  }
}

export default {
  name: 'AddPanel',

  components: {
    Popup,
    Tabs,
    AddPanelBasic,
    AddPanelNetwork,
    AddPanelAccess,
    AddPanelUsers,
  },

  mixins: [ServiceData],

  data() {
    return {
      isSubmitting: false,
      isGeneratingLink: false,
    }
  },

  computed: {
    currentAddress() {
      return this.$store.getters.currentAddress
    },
    currentStep() {
      return this.$store.getters.currentStep
    },
    steps() {
      return this.$store.getters.steps
    },
    isEditMode() {
      return this.$store.getters.isEditMode
    },
    editingPanelId() {
      return this.$store.getters.editingPanelId
    },
    panelData() {
      return this.$store.getters.panelData
    },
    existingPanels() {
      return this.$store.getters.panels
    },
    currentOrganizationId() {
      return this.$store.getters.currentOrganizationId
    },
    availableSections() {
      return this.$store.getters.availableSections
    },

    popupTitle() {
      return this.isEditMode ? 'Редактирование панели' : 'Добавление панели'
    },
    submitButtonText() {
      return this.isEditMode ? 'Обновить' : 'Сохранить'
    },
  },

  methods: {
    buildTransformedPayload() {
      const { basic, network, access, users } = this.panelData
      const credentials = resolveCredentials(network)

      const devices = access.filter(a => a.deviceTypeId).map(buildDevicePayload)

      const sections = users.available_to_all_residents
        ? []
        : users.selectedSections.map(id =>
            buildSectionPayload(id, this.availableSections)
          )

      const payload = {
        organization_id: this.currentOrganizationId,
        manufacturer_id: network.manufacturer,
        name: basic.name || '',
        sip_account: basic.sipAccount || '',
        ip_address: network.ipAddress || '',
        rtsp_url: network.rtspUrl || '',
        login: credentials.login,
        apply_credentials_to_all: network.applyToAll,
        devices,
        sections,
        available_to_all_residents: users.available_to_all_residents,
        is_active: true,
      }

      // keep the stored password when the field is left empty on edit
      if (!this.isEditMode || credentials.password) {
        payload.password = credentials.password
      }

      return payload
    },

    validate() {
      const credentials = resolveCredentials(this.panelData.network)
      const panelForValidation = {
        ...this.panelData,
        network: { ...this.panelData.network, ...credentials },
      }

      const result = validatePanelData(
        panelForValidation,
        this.existingPanels,
        this.editingPanelId
      )

      if (!result.isValid) {
        this.$store.dispatch('setNotification', {
          type: 'cross-outlined',
          text: getErrorMessage(result.errors),
        })

        const errorStep = getFirstErrorStep(result.errors)
        if (errorStep) this.$store.dispatch('setCurrentStep', errorStep)
      }

      return result.isValid
    },

    async handleSubmit() {
      if (this.isSubmitting || !this.validate()) return

      this.isSubmitting = true

      try {
        const cuid = this.$route.params.intercomId
        const payload = this.buildTransformedPayload()

        const response =
          this.isEditMode && this.editingPanelId
            ? await this.updatePanel(cuid, this.editingPanelId, payload)
            : await this.createPanel(cuid, payload)

        if (response?.data) {
          const action = this.isEditMode ? 'updatePanel' : 'addNewPanel'
          this.$store.dispatch(action, response.data)
        }

        this.$store.dispatch('resetPanelForm')
        this.$emit('close')
      } catch (error) {
        console.error('Error saving panel:', error)
      } finally {
        this.isSubmitting = false
      }
    },

    async handleGenerateLink({
      cuid,
      panel_id,
      device_id,
      organization_id,
      callback,
    }) {
      this.isGeneratingLink = true

      try {
        const response =
          panel_id && device_id && organization_id
            ? await this.generatePanelLink(
                cuid,
                panel_id,
                device_id,
                organization_id
              )
            : await this.generateDeviceAccessLink()

        const link = response?.data?.open_link || ''

        this.$store.dispatch(
          'setNotification',
          link
            ? { type: 'check-outlined', text: 'Ссылка успешно сгенерирована' }
            : {
                type: 'cross-outlined',
                text: 'Не удалось сгенерировать ссылку',
              }
        )

        callback?.(link)
        return link
      } catch (error) {
        console.error('Error generating access link:', error)

        const text =
          error.response?.data?.message || 'Ошибка при генерации ссылки'
        this.$store.dispatch('setNotification', {
          type: 'cross-outlined',
          text,
        })

        callback?.('')
        return ''
      } finally {
        this.isGeneratingLink = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.add-panel {
  display: flex;
  flex-direction: column;
  gap: 32px;

  &__actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
  }
}
</style>
